import { Funnel, FunnelX, Pin, RotateCcw } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import {
  getCountyOptions,
  getDepartmentOptions,
  getMarketOptions,
  getRegionOptions,
  pruneScopeFilters,
  toggleScopeValue,
} from '../../scope';
import ScopeChip from './ScopeChip';
import styles from './ScopeFilterGroup.module.scss';

const toOptionList = (values) => values.map((value) => ({ value, label: value }));

const ScopeFilterGroup = ({
  departments,
  filters,
  onChangeFilters,
  guideEnabled,
  onToggleGuide,
  departmentToneById = {},
  showUtilities = false,
  isPinned = false,
  onTogglePin,
  canReset = false,
  onReset,
  isPaused = false,
  onTogglePause,
}) => {
  const regionOptions = toOptionList(getRegionOptions(departments));
  const countyOptions = toOptionList(getCountyOptions(departments, filters));
  const marketOptions = toOptionList(getMarketOptions(departments, filters));
  const departmentOptions = getDepartmentOptions(departments, filters).map((department) => ({
    value: department.id,
    label: department.label,
    tone: departmentToneById[department.id],
  }));

  const updateLevel = (level, value) => {
    const nextFilters = toggleScopeValue(filters, level, value);
    onChangeFilters(pruneScopeFilters(departments, nextFilters));
  };

  const clearLevel = (level) => {
    onChangeFilters(pruneScopeFilters(departments, { ...filters, [level]: [] }));
  };

  return (
    <div className={styles.root} role="group" aria-label="Scope filters">
      <ScopeChip
        label="Region"
        options={regionOptions}
        selectedValues={filters.regions}
        onToggle={(value) => updateLevel('regions', value)}
        onClear={() => clearLevel('regions')}
      />
      <ScopeChip
        label="County"
        options={countyOptions}
        selectedValues={filters.counties}
        onToggle={(value) => updateLevel('counties', value)}
        onClear={() => clearLevel('counties')}
      />
      <ScopeChip
        label="Market"
        options={marketOptions}
        selectedValues={filters.markets}
        onToggle={(value) => updateLevel('markets', value)}
        onClear={() => clearLevel('markets')}
      />
      <ScopeChip
        label="Department"
        options={departmentOptions}
        selectedValues={filters.departments}
        onToggle={(value) => updateLevel('departments', value)}
        onClear={() => clearLevel('departments')}
      />
      <button
        type="button"
        role="switch"
        aria-checked={guideEnabled}
        title="Only GUIDE-enrolled members"
        onClick={() => onToggleGuide(!guideEnabled)}
        className={`${styles.guideToggle} ${guideEnabled ? styles.guideToggleActive : ''}`}
      >
        GUIDE
      </button>
      {showUtilities && (
        <>
          <span className={styles.divider} aria-hidden="true" />
          <div className={styles.utilities}>
            <button
              type="button"
              title={isPinned ? 'Default filters — click to unpin' : 'Save as my default filters'}
              aria-label={isPinned ? 'Unpin default filters' : 'Save current filters as default'}
              aria-pressed={isPinned}
              onClick={onTogglePin}
              className={`${styles.utilityButton} ${isPinned ? styles.utilityButtonPinned : ''}`}
            >
              <Pin
                size={14}
                strokeWidth={2}
                fill={isPinned ? 'currentColor' : 'none'}
                aria-hidden="true"
              />
            </button>
            {canReset && (
              <button
                type="button"
                title="Back to your saved default"
                onClick={onReset}
                className={styles.resetButton}
              >
                <RotateCcw size={12} strokeWidth={2} aria-hidden="true" />
                Reset
              </button>
            )}
            <button
              type="button"
              role="switch"
              aria-checked={!isPaused}
              aria-label="Apply geographic filters"
              title={
                isPaused
                  ? 'Resume filters'
                  : 'Pause filters — see everything without losing your setup'
              }
              onClick={onTogglePause}
              className={`${styles.utilityButton} ${isPaused ? styles.utilityButtonPaused : ''}`}
            >
              {isPaused ? (
                <FunnelX size={14} strokeWidth={2} aria-hidden="true" />
              ) : (
                <Funnel size={14} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

ScopeFilterGroup.propTypes = {
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      regionName: PropTypes.string,
      countyName: PropTypes.string,
      marketName: PropTypes.string,
    }),
  ).isRequired,
  filters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string).isRequired,
    counties: PropTypes.arrayOf(PropTypes.string).isRequired,
    markets: PropTypes.arrayOf(PropTypes.string).isRequired,
    departments: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  guideEnabled: PropTypes.bool.isRequired,
  onToggleGuide: PropTypes.func.isRequired,
  departmentToneById: PropTypes.object,
  showUtilities: PropTypes.bool,
  isPinned: PropTypes.bool,
  onTogglePin: PropTypes.func,
  canReset: PropTypes.bool,
  onReset: PropTypes.func,
  isPaused: PropTypes.bool,
  onTogglePause: PropTypes.func,
};

export default ScopeFilterGroup;
